/**
 * What the browser knows and the host cannot see.
 *
 * `propmap prod` emits a directory of files; the client is the whole
 * application. That is what makes the product cheap to serve and also what
 * leaves it unobserved: a static host's log says a byte range was read, and
 * nothing about whether a map appeared, how long it took to appear, or which of
 * the four categories anyone actually moved.
 *
 * Nothing here opens a connection on a schedule. Counts accumulate in memory
 * and ride out on a header attached to a request the app was making anyway --
 * a raster, a points file, the outlines -- at most once a minute. Two cases
 * cannot wait for one, and only those two are pushed: a page that has just
 * thrown may never fetch again, and this app can run for a very long time on
 * nothing but the GPU, because every slider, tap and panel is local arithmetic
 * over bytes that arrived with the first paint. So a report that has been
 * waiting longer than `STALE_MS` stops waiting.
 *
 * What goes on the wire is a delta since the last flush, never a running total.
 * A collector adds those into one counter per metric, which means a flush that
 * never arrives is an undercount rather than a series that steps backwards, and
 * costs it no per-client state to difference against.
 *
 * Nothing collected here identifies anyone: counts of things that happened, and
 * two durations. There is no area code, no coordinate and no weight in it -- the
 * query string holds all of that, and it is the reader's to share or not.
 */

/** The request header a flush rides out on. */
export const METRICS_HEADER = "X-Propmap-Client";

/**
 * Where a pushed flush goes.
 *
 * Caddy answers it with an empty 204 and logs it like any other request, and
 * the flush travels in the same header as a ride-along one, so the access log
 * is the one place both kinds arrive and the collector reads them from there
 * (`ops/stack/collector/`). The dev server has nothing at this path, so a dev
 * build prints the flush instead of posting it into a 404.
 */
const ENDPOINT = "/client-metrics";

/** How long a flush waits for a request to ride out on. */
const FLUSH_INTERVAL_MS = 60_000;

/** How long a due flush waits before it stops expecting one and pushes. */
const STALE_MS = 5 * 60_000;

/**
 * The least time between two pushed flushes. A page that throws once tends to
 * throw repeatedly, and the report is worth one request, not one per exception.
 */
const PUSH_INTERVAL_MS = 30_000;

/**
 * The longest stretch without input that still counts as time on the page. A
 * reader studying the panel is still there; a tab left open on a second monitor
 * over lunch is not, and without a ceiling it would be the longest session of
 * the day.
 */
const IDLE_MS = 5 * 60_000;

/** Pointer movement is continuous; crediting it more than this often is noise. */
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Session lengths, as one flag per page for each of these it passes. Counted
 * against page views that is the share of visits longer than each, which stays
 * right when a reader leaves for Rightmove and comes back -- the common case,
 * and the one a single length sent at the first hide would cut short.
 */
const SESSION_MARKS: readonly (readonly [number, keyof Pending])[] = [
  [10, "oa"], [30, "ob"], [60, "oc"], [180, "od"], [600, "oe"], [1800, "of"],
];

/** Above this, a wait for an asset is one somebody noticed. */
const SLOW_ASSET_MS = 1500;

/**
 * Two slider events on the same category closer together than this are one
 * gesture. A dragged range input fires per pixel, and what is worth counting is
 * that somebody weighted a category, not how far their thumb travelled. Two
 * categories in quick succession are still two things done, which is why the
 * window is per category and not a bare timer.
 */
const GESTURE_MS = 500;

/**
 * The wire names. Two characters because they ride on requests for files that
 * are themselves only a few kilobytes, and fixed because a collector knows
 * them: a name changed here without changing it there is a metric that silently
 * stops arriving.
 */
interface Pending {
  /** asset requests made */
  aq: number;
  /** of those, slower than SLOW_ASSET_MS */
  as: number;
  /** of those, that failed or answered with an error status */
  ae: number;
  /** uncaught errors and rejected promises */
  ee: number;
  /** slider gestures */
  wt: number;
  /** category layers opened */
  vw: number;
  /** changes of which schools count */
  ph: number;
  /** areas opened by a tap */
  ar: number;
  /** evidence markers opened -- a crime dot, a school, a station */
  ev: number;
  /** ad slots filled */
  ai: number;
  /** ads dismissed with the close control */
  ac: number;
  /** of the ads shown, those an ad blocker replaced with the banner */
  ab: number;
  /** of the ads shown, those the provider had nothing for */
  au: number;
  /** clicks out to Rightmove, to Zoopla, and to Street View */
  lr: number;
  lz: number;
  ls: number;
  /** the area panel's per-category detail lines opened */
  dt: number;
  /** taps that found no area: the Thames, the edge of London, beyond it */
  am: number;
  /** areas opened that the current weights leave off the map */
  ao: number;
  /** zooms in past street level, where the evidence splits into points */
  zs: number;
  /** pages that opened on a link carrying a state: shared, bookmarked, or sent */
  sl: number;
  /** pages whose basemap could not be reached, drawn on a plain ground */
  bo: number;
  /** WebGL contexts the browser took away, which blanks the map */
  gl: number;
  /** pages that never drew a map at all */
  sf: number;
  /** page views, one per page, by the device it opened on */
  pm: number;
  pt: number;
  pd: number;
  /** scenario presets chosen: balanced, family, student, commuter, quiet life */
  rb: number;
  rf: number;
  rs: number;
  rc: number;
  rq: number;
  /** seconds of active, visible time on the page since the last report */
  ss: number;
  /** this page's session passed 10 s, 30 s, 1 min, 3 min, 10 min, 30 min */
  oa: number;
  ob: number;
  oc: number;
  od: number;
  oe: number;
  of: number;
}

const empty = (): Pending => ({
  aq: 0, as: 0, ae: 0, ee: 0, wt: 0, vw: 0, ph: 0, ar: 0, ev: 0, ai: 0, ac: 0,
  ab: 0, au: 0, lr: 0, lz: 0, ls: 0, dt: 0, am: 0, ao: 0, zs: 0, sl: 0, bo: 0, gl: 0, sf: 0,
  pm: 0, pt: 0, pd: 0, rb: 0, rf: 0, rs: 0, rc: 0, rq: 0,
  ss: 0, oa: 0, ob: 0, oc: 0, od: 0, oe: 0, of: 0,
});

let pending = empty();

/** Measured once, and cleared onto the first flush that carries it. */
let firstPaintMs: number | null = null;

/**
 * Milliseconds from the map appearing to the reader first doing anything with
 * it. Measured once, like the paint, and only once there has been a paint: an
 * action before it is somebody fighting a page that has not arrived.
 */
let firstActionMs: number | null = null;
let paintedAt: number | null = null;
let acted = false;

/*
 * The clock starts when the page loads, not at the epoch. Zero would mean every
 * client is infinitely overdue at the moment it opens, and the first thing
 * anybody did would be pushed out on its own rather than waiting for the
 * request that was coming anyway. What must not wait -- the first paint, an
 * error -- says so by setting `urgent`.
 */
let lastFlush = Date.now();
let lastPush = 0;
let lastGesture = { scope: "", at: -Infinity };

/** Set by anything that should not wait out the interval to be heard. */
let urgent = false;

/*
 * Session time, on the wall clock like everything else here. `lastMark` is null
 * while the page is hidden; each gap between marks is credited up to IDLE_MS.
 */
let activeMs = 0;
let reportedMs = 0;
let lastMark: number | null = null;
let marksPassed = 0;

/** What the reader did, as the rest of the app reports it. */
export type Interaction = "weight" | "view" | "phase" | "area" | "evidence";

const COUNTER: Record<Interaction, keyof Pending> = {
  weight: "wt",
  view: "vw",
  phase: "ph",
  area: "ar",
  evidence: "ev",
};

type InteractionListener = (kind: Interaction) => void;

const listeners: InteractionListener[] = [];

/**
 * Hear about interactions as they are counted.
 *
 * One stream with two readers: these counters, and the ad slot, which measures
 * its interval in the same actions. Registering here is what keeps the two from
 * disagreeing about what an action is -- in particular about a slider drag,
 * which is one of them however many events it fires.
 */
export function onInteraction(listener: InteractionListener): void {
  listeners.push(listener);
}

/**
 * Something the reader did.
 *
 * Weight changes are coalesced into gestures within `scope`, which for a slider
 * is the category it belongs to; everything else is discrete already. A
 * coalesced event is not an action at all -- it does not reach the listeners
 * either, so a drag advances the ad's counter by one.
 */
export function noteInteraction(kind: Interaction, now = Date.now(), scope = ""): void {
  if (kind === "weight") {
    const same = lastGesture.scope === scope && now - lastGesture.at < GESTURE_MS;
    lastGesture = { scope, at: now };
    if (same) return;
  }
  pending[COUNTER[kind]] += 1;
  if (!acted && paintedAt !== null) {
    acted = true;
    firstActionMs = Math.round(performance.now() - paintedAt);
  }
  for (const listener of listeners) listener(kind);
  // The only regular chance to notice that a report has been sitting on a
  // client that has stopped asking for files, which after the first seconds is
  // the normal state of this app.
  if (isStale(now)) push(now);
}

/** An asset request that answered, however long it took. */
export function countAsset(ms: number): void {
  pending.aq += 1;
  if (ms > SLOW_ASSET_MS) pending.as += 1;
}

/**
 * An asset request that did not answer usefully.
 *
 * Every one of these is visible: the first paint fails outright, and a later
 * one is a category whose evidence never appears.
 */
export function countAssetError(): void {
  pending.aq += 1;
  pending.ae += 1;
  urgent = true;
}

export function countAdShown(): void {
  pending.ai += 1;
}

export function countAdClosed(): void {
  pending.ac += 1;
}

/** An ad slot that opened on the banner, because a blocker ate the ad. */
export function countAdBlocked(): void {
  pending.ab += 1;
}

/** An ad request the provider answered with nothing, so the slot closed. */
export function countAdUnfilled(): void {
  pending.au += 1;
}

/** Where a place's links lead: the listings, and a look at the street. */
export type Outbound = "rightmove" | "zoopla" | "streetview";

const OUTBOUND: Record<Outbound, keyof Pending> = {
  rightmove: "lr",
  zoopla: "lz",
  streetview: "ls",
};

/**
 * The reader left for a listings site or Street View.
 *
 * Not an interaction: it does not advance the ad's counter, because somebody
 * following a link is on their way out, and the tab they come back to should not
 * greet them with an ad for having found a house.
 */
export function countOutbound(site: string): void {
  const key = OUTBOUND[site as Outbound];
  if (key) pending[key] += 1;
}

/** The scenario chips, by name. A name not listed here is not counted. */
const PRESET: Record<string, keyof Pending> = {
  balanced: "rb",
  family: "rf",
  student: "rs",
  commuter: "rc",
  quiet: "rq",
};

/**
 * A scenario chip pressed. Counted at the press rather than read off the
 * weights, so pressing the chip that is already on still says which scenario
 * somebody reached for.
 */
export function countPreset(name: string): void {
  const key = PRESET[name];
  if (key) pending[key] += 1;
}

export type Device = "mobile" | "tablet" | "desktop";

const DEVICE: Record<Device, keyof Pending> = { mobile: "pm", tablet: "pt", desktop: "pd" };

/**
 * What the page opened on, from what the browser says about itself.
 *
 * Not the layout: a narrow desktop window gets the phone layout and is still
 * somebody at a desk. `navigator.userAgentData.mobile` is the browser's own
 * answer where it gives one (Chromium); elsewhere the user agent string. iPadOS
 * reports itself as a Mac, and only its touch screen gives it away.
 */
export function deviceOf(ua: string, uaMobile: boolean | undefined, touchPoints: number): Device {
  if (uaMobile === true) return "mobile";
  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return "tablet";
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return "tablet";
  if (/Macintosh/.test(ua) && touchPoints > 1) return "tablet";
  if (/Mobi|iPhone|iPod|Android|Windows Phone/i.test(ua)) return "mobile";
  return "desktop";
}

/** This page, counted once, as the device it opened on. */
export function countPageView(device: Device): void {
  pending[DEVICE[device]] += 1;
}

/** Credit the time since the last mark, up to the idle ceiling. */
function credit(now: number): void {
  if (lastMark === null) return;
  activeMs += Math.max(0, Math.min(now - lastMark, IDLE_MS));
  lastMark = now;
}

/** Input of any kind: the reader is still here. */
export function noteActivity(now = Date.now()): void {
  if (lastMark !== null && now - lastMark >= ACTIVITY_THROTTLE_MS) credit(now);
}

/**
 * The page was shown or hidden. Hiding is the end of a session as far as can
 * be known -- a phone may kill a hidden tab without another event -- so it is
 * also when the session's time is pushed.
 */
export function noteVisibility(visible: boolean, now = Date.now()): void {
  if (visible) {
    if (lastMark === null) lastMark = now;
    return;
  }
  credit(now);
  lastMark = null;
  push(now, true);
}

/** Move the session's credited time, and any marks it has passed, onto the report. */
function settleSession(): void {
  const seconds = Math.floor((activeMs - reportedMs) / 1000);
  if (seconds > 0) {
    // the collector's ceiling per report; anything above it is a clock gone wrong
    pending.ss += Math.min(seconds, 10_000);
    reportedMs += seconds * 1000;
  }
  while (marksPassed < SESSION_MARKS.length && activeMs >= SESSION_MARKS[marksPassed]![0] * 1000) {
    pending[SESSION_MARKS[marksPassed]![1]] += 1;
    marksPassed += 1;
  }
}

export function countDetailOpened(): void {
  pending.dt += 1;
}

export function countTapOutside(): void {
  pending.am += 1;
}

export function countAreaOffMap(): void {
  pending.ao += 1;
}

export function countStreetZoom(): void {
  pending.zs += 1;
}

export function countSharedLink(): void {
  pending.sl += 1;
}

/** Visible, but not urgent: the map still works, it only lacks its streets. */
export function countBasemapFallback(): void {
  pending.bo += 1;
}

/**
 * The map has gone blank. The page may never fetch again, so it is pushed
 * rather than left to ride.
 */
export function countContextLost(): void {
  pending.gl += 1;
  push();
}

/**
 * The page is showing an error instead of a map. That is caught, so the error
 * listeners never hear it, and nothing will be fetched after it -- so this is
 * pushed at once, past the push interval.
 */
export function countStartFailure(): void {
  pending.sf += 1;
  push(Date.now(), true);
}

function hasSomethingToSay(): boolean {
  return firstPaintMs !== null || firstActionMs !== null || Object.values(pending).some((n) => n > 0);
}

function isStale(now: number): boolean {
  return hasSomethingToSay() && now - lastFlush > STALE_MS;
}

/**
 * The header for the next request, or null to send it without one.
 *
 * Reading it is what clears it, so a flush is offered to exactly one request.
 * If that request fails the counts are lost, which is the same undercount as a
 * tab closing mid-interval and is not worth the bookkeeping to avoid.
 */
export function flushHeader(now = Date.now()): string | null {
  settleSession();
  if (!hasSomethingToSay()) return null;
  if (!urgent && now - lastFlush < FLUSH_INTERVAL_MS) return null;

  const parts: string[] = [];

  if (firstPaintMs !== null) parts.push(`fp=${firstPaintMs}`);
  if (firstActionMs !== null) parts.push(`fa=${firstActionMs}`);

  for (const [key, value] of Object.entries(pending)) {
    if (value > 0) parts.push(`${key}=${value}`);
  }

  firstPaintMs = null;
  firstActionMs = null;
  pending = empty();
  lastFlush = now;
  urgent = false;

  return parts.join(",");
}

/**
 * Hand a flush to the collector, or to the console while there is none.
 *
 * Kept apart from the ride-along so that the two ways out of the browser are
 * one line each to change when the receiving end exists.
 */
function deliver(body: string): void {
  if (dev()) {
    console.debug(`[propmap metrics] ${body}`);
    return;
  }
  // `keepalive` is what survives the page being closed, which is the case the
  // pagehide flush exists for. Not a beacon: a beacon cannot carry a header,
  // and the header is the only part of a request Caddy's log records.
  void fetch(ENDPOINT, { method: "POST", headers: { [METRICS_HEADER]: body }, keepalive: true })
    .catch(() => {
      /* The counts are gone. This was the attempt to save them, not a guarantee. */
    });
}

/**
 * Send what is pending without waiting for the app to make a request.
 *
 * For the cases the riding-along scheme cannot cover: a page that has thrown
 * may never fetch anything again, a page being closed will not fetch again by
 * definition, and a reader who has settled into moving sliders may not fetch
 * anything for an hour.
 */
function push(now = Date.now(), force = false): void {
  if (!force && now - lastPush < PUSH_INTERVAL_MS) return;

  urgent = true;

  const header = flushHeader(now);

  if (header === null) return;

  lastPush = now;
  deliver(header);
}

function dev(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

/** Everything waiting to go out, for the console handle and for tests. */
export function snapshot(now = Date.now()) {
  return {
    ...pending,
    firstPaintMs,
    firstActionMs,
    urgent,
    sinceFlushMs: now - lastFlush,
    endpoint: ENDPOINT,
  };
}

/** Forget everything, including the timers. Tests and the console only. */
export function reset(now = Date.now()): void {
  pending = empty();
  firstPaintMs = null;
  firstActionMs = null;
  paintedAt = null;
  acted = false;
  lastFlush = now;
  lastPush = 0;
  lastGesture = { scope: "", at: -Infinity };
  urgent = false;
  activeMs = 0;
  reportedMs = 0;
  lastMark = now;
  marksPassed = 0;
}

/**
 * The map is on screen.
 *
 * Two frames, because one only proves the browser is about to paint. The second
 * runs after the first frame is on screen, which is the moment the map exists
 * for the person waiting for it.
 *
 * performance.now() is milliseconds since the navigation started, so it is the
 * whole arrival -- bundle, manifest, four rasters and the lattice -- without
 * any need to subtract a start.
 */
export function notePaint(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      paintedAt = performance.now();
      firstPaintMs = Math.round(paintedAt);
      urgent = true;
    });
  });
}

/**
 * Start collecting. Called once, before anything is fetched, so that a failure
 * during the first paint is itself counted.
 */
export function installClientMetrics(): void {
  const host = window as unknown as Record<string, unknown>;
  host["__propmapMetrics"] = { snapshot, reset, flush: () => push(Date.now(), true) };

  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  countPageView(deviceOf(nav.userAgent, nav.userAgentData?.mobile, nav.maxTouchPoints ?? 0));

  // The session starts with the navigation, so the wait for the map counts.
  lastMark = document.visibilityState === "visible" ? Date.now() - performance.now() : null;
  document.addEventListener("visibilitychange", () =>
    noteVisibility(document.visibilityState === "visible"));
  for (const type of ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"]) {
    window.addEventListener(type, () => noteActivity(), { passive: true, capture: true });
  }

  window.addEventListener("error", () => {
    pending.ee += 1;
    push();
  });

  window.addEventListener("unhandledrejection", () => {
    pending.ee += 1;
    push();
  });

  // Not `unload`, which a page restored from the back/forward cache never gets
  // and which some browsers no longer fire at all.
  window.addEventListener("pagehide", () => {
    if (lastMark !== null) noteVisibility(false);
    else push(Date.now(), true);
  });
}
